/**
 * 인증 도메인 타입.
 *
 * 백엔드 common/security/dto 와 1:1로 대응한다.
 * 한쪽을 바꾸면 반드시 다른 쪽도 바꾼다.
 */

/** 접근 가능한 화면 하나. 백엔드 MenuResponse. */
export interface Menu {
  /** 권한이 참조하는 불변 식별자. 경로가 바뀌어도 유지된다. */
  code: string;
  name: string;
  /** 라우트 경로. 예: /trading/account */
  path: string;
}

/**
 * 현재 로그인한 사용자. 백엔드 MeResponse.
 *
 * menus 에는 이 사용자가 접근 가능한 메뉴만 들어 있다.
 * 내비게이션과 ProtectedRoute 가 이 목록으로 판단한다.
 *
 * 🔴 이 목록은 화면 편의를 위한 것이다. 실제 차단은 백엔드가 한다.
 *   브라우저에서 값을 고쳐 다른 화면을 열어도 API 가 403 을 준다.
 */
export interface Me {
  id: number;
  loginId: string;
  displayName: string;
  /** 본인의 이메일. 없으면 null. 내 정보 화면이 채워 보여준다 */
  email: string | null;
  admin: boolean;
  menus: Menu[];
}

export interface LoginRequest {
  loginId: string;
  password: string;
  /** 로그인 유지. true 면 30일 영속 쿠키가 발급된다. */
  rememberMe: boolean;
}

/**
 * 인증 관련 에러 코드. 백엔드 ErrorCode 의 A 대역과 같다.
 *
 * 화면이 코드로 분기하는 이유는 메시지가 바뀔 수 있기 때문이다.
 * 메시지 문자열로 비교하면 백엔드에서 오타 하나만 고쳐도 분기가 깨진다.
 */
export const AUTH_ERROR_CODES = {
  /** 아이디 또는 비밀번호 오류. 둘을 구분하지 않는다 */
  LOGIN_FAILED: 'A001',
  ACCOUNT_LOCKED: 'A002',
  ACCOUNT_INACTIVE: 'A003',
  /** 세션이 없거나 만료됐다. 로그인 페이지로 보낸다 */
  UNAUTHENTICATED: 'A004',
  /** 로그인은 됐지만 그 화면 권한이 없다. 다시 로그인해도 소용없다 */
  ACCESS_DENIED: 'A005',
  /** 본인 비밀번호 변경에서 현재 비밀번호가 틀렸다. 로그인 실패(A001)와 다른 자리다 */
  CURRENT_PASSWORD_MISMATCH: 'A011',
} as const;

/** 본인 정보 수정. 백엔드 UpdateProfileRequest. */
export interface UpdateProfileRequest {
  displayName: string;
  /** 비우면 null 로 저장된다. email 에 UNIQUE 가 걸려 있어 빈 문자열로 두면 안 된다 */
  email: string | null;
}

/**
 * 본인 비밀번호 변경. 백엔드 ChangePasswordRequest.
 *
 * 🔴 관리자 재설정과 달리 현재 비밀번호를 반드시 보낸다.
 *   이미 로그인했는데 왜 또 묻느냐면, 로그인은 "그때 본인이었다" 이고
 *   여기서 묻는 것은 "지금도 본인인가" 이기 때문이다.
 *   빼면 세션 탈취 한 번으로 주인이 영구히 잠긴다.
 */
export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}
